import type { ResolvedConfig } from "./config";
import type { CategoryResponse } from "./api-client";

// ---------------------------------------------------------------------------
// Event types
// ---------------------------------------------------------------------------

export interface SdkEventMap {
  open: void;
  close: void;
  "submit-start": void;
  "submit-success": void;
  "submit-error": Error;
}

export type SdkEventName = keyof SdkEventMap;
type Callback<T> = T extends void ? () => void : (data: T) => void;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export class SdkState {
  isOpen = false;
  categories: CategoryResponse[] = [];
  isSubmitting = false;
  config: ResolvedConfig | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private listeners = new Map<SdkEventName, Set<Callback<any>>>();

  on<E extends SdkEventName>(event: E, callback: Callback<SdkEventMap[E]>): void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(callback);
  }

  off<E extends SdkEventName>(event: E, callback: Callback<SdkEventMap[E]>): void {
    this.listeners.get(event)?.delete(callback);
  }

  emit<E extends SdkEventName>(
    event: E,
    ...args: SdkEventMap[E] extends void ? [] : [SdkEventMap[E]]
  ): void {
    const set = this.listeners.get(event);
    if (!set) return;
    const data = args[0];
    for (const cb of set) {
      try {
        cb(data);
      } catch {
        // listener errors should not break the SDK
      }
    }
  }

  reset(): void {
    this.isOpen = false;
    this.categories = [];
    this.isSubmitting = false;
    this.config = null;
    this.listeners.clear();
  }
}

// Singleton
export const sdkState = new SdkState();
